// ===========================================
// BUSTED! BLACKJACK - CHIP PURCHASE MODAL
// ===========================================

import { useState, useEffect } from 'react';
import { api, ChipPackage } from '../api/client';
import { BahtSymbol } from './Currency';
import { Modal } from './Modal';

interface ChipPurchaseModalProps {
  onClose: () => void;
}

export function ChipPurchaseModal({ onClose }: ChipPurchaseModalProps) {
  const [packages, setPackages] = useState<ChipPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch packages on mount
  useEffect(() => {
    async function loadPackages() {
      const result = await api.payments.getPackages();
      if (result.success && result.data) {
        setPackages(result.data.packages);
      } else {
        setError('Failed to load packages');
      }
      setIsLoading(false);
    }
    loadPackages();
  }, []);

  const handlePurchase = async (packageId: string) => {
    setSelectedPackage(packageId);
    setIsCheckingOut(true);
    setError(null);

    const result = await api.payments.createCheckout(packageId);

    if (result.success && result.data?.url) {
      // Redirect to Stripe Checkout
      window.location.href = result.data.url;
    } else {
      setError(result.error?.message || 'Failed to create checkout session');
      setIsCheckingOut(false);
      setSelectedPackage(null);
    }
  };

  const formatChips = (chips: number): string => {
    if (chips >= 1000000) {
      return (chips / 1000000).toFixed(chips % 1000000 === 0 ? 0 : 1) + 'M';
    }
    if (chips >= 1000) {
      return (chips / 1000).toFixed(chips % 1000 === 0 ? 0 : 1) + 'K';
    }
    return chips.toLocaleString();
  };

  // Prevent close during checkout
  const handleClose = () => {
    if (!isCheckingOut) {
      onClose();
    }
  };

  return (
    <Modal
      variant="default"
      blocking={true}
      title="Buy B-Bucks"
      subtitle="Choose a package to purchase"
      onClose={handleClose}
      closeOnEsc={!isCheckingOut}
      closeOnOverlayClick={!isCheckingOut}
      maxWidthClass="max-w-4xl"
    >
      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[70vh]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-casino-gold-500"></div>
          </div>
        ) : error && packages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-red-400">{error}</p>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Error message */}
            {error && (
              <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            {/* Package Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`relative rounded-xl p-4 border-2 transition-all cursor-pointer ${
                    pkg.isPopular
                      ? 'bg-gradient-to-br from-casino-gold-500/20 to-amber-600/20 border-casino-gold-500'
                      : 'bg-gray-700/50 border-gray-600 hover:border-gray-500'
                  } ${
                    selectedPackage === pkg.id ? 'ring-2 ring-casino-gold-500' : ''
                  } ${
                    isCheckingOut && selectedPackage !== pkg.id ? 'opacity-50' : ''
                  }`}
                  onClick={() => !isCheckingOut && handlePurchase(pkg.id)}
                >
                  {/* Popular badge */}
                  {pkg.isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-casino-gold-500 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </div>
                  )}

                  {/* Bonus badge */}
                  {pkg.bonusPercent > 0 && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      +{pkg.bonusPercent}%
                    </div>
                  )}

                  {/* Package name */}
                  <h3 className={`text-lg font-bold mb-2 ${pkg.isPopular ? 'text-casino-gold-400' : 'text-white'}`}>
                    {pkg.name}
                  </h3>

                  {/* Chips amount */}
                  <div className="mb-3">
                    <span className="text-3xl font-bold text-casino-gold-400">
                      <BahtSymbol />{formatChips(pkg.chips)}
                    </span>
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-white">
                      ${pkg.priceUsd}
                    </span>
                    <span className="text-xs text-gray-400">
                      {pkg.chipsPerDollar.toLocaleString()} chips per $1
                    </span>
                  </div>

                  {/* Loading spinner */}
                  {isCheckingOut && selectedPackage === pkg.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800/80 rounded-xl">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-casino-gold-500"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Security notice */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                </svg>
                <span>Secure payment powered by Stripe</span>
              </div>
              <p>B-Bucks are virtual play chips for entertainment only. They have no cash value and cannot be redeemed, withdrawn, or exchanged for money.</p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ChipPurchaseModal;
